import { DOCUMENT } from '@angular/common';
import { inject, Injectable, TransferState } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { REQUEST } from '../../../express.tokens';
import { IS_BROWSER_TOKEN } from '../tokens/is-browser.token';
import { IS_SERVER_TOKEN } from '../tokens/is-server.token';
import { IS_SSR_MODE_TOKEN } from '../tokens/is-ssr-mode.token';
import { LoggerService } from './logger.service';
import { SITE_CONFIG_KEY } from '../tokens/site-config-key.token';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../tokens/generator-env.token';
import { parseQueryPqrams, removeUrlQueryParam } from '@techmore/global';
import { BackStagePayload } from '../entity/back-stage-payload';
import { environment } from 'src/environments/environment';
import { removeAnchorHref } from 'src/app/util';

@Injectable({
  providedIn: 'root'
})
export class ServerConfigService {
  protected request = inject(REQUEST, { optional: true }) ?? null;
  protected tState = inject(TransferState, { optional: true }) ?? null;
  protected readonly loggerService: LoggerService = inject(LoggerService);
  protected readonly document: Document = inject(DOCUMENT);
  protected generatorEnv: GeneratorEnv = inject(GENERATOR_ENV_TOKEN);
  /** 是否為客戶端渲染 */
  public isBrowser = inject(IS_BROWSER_TOKEN);
  /** 是否為NODE端渲染 */
  public isServer = inject(IS_SERVER_TOKEN);
  /** 是否為 SSR 模式 */
  public isSSRMode = inject(IS_SSR_MODE_TOKEN);
  /** 網頁請求的url (ex: http://localhost:4200/home/zh-tw/participate/75?stage=aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA%3D)
   * !! 注意 Host 可偽造，只用作紀錄日誌不可用來寫入到文件中。
   * 要寫入到文件中使用 siteUrl，它的 host 是使用環境設定中的站點名稱防止偽造連結。
   * 在共構的模式中是根據 host 取得對應 domain 的網站資料，若 host 偽造是取不到資料的，所以在共構案中不使用(也無法使用)環境設定檔去給定一個 serverHost，實際同 url 的組法。
   */
  public url: string;
  /** 訪問的網頁url (ex: http://localhost:4200/home/zh-tw/participate/75?stage=aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA%3D)
   * 寫入到文件中使用 siteUrl，它的 host 是使用環境設定中的站點名稱防止偽造連結。
   * 在共構的模式中是根據 host 取得對應 domain 的網站資料，若 host 偽造是取不到資料的，所以在共構案中不使用(也無法使用)環境設定檔去給定一個 serverHost，實際同 url 的組法。
   */
  public siteUrl: string;
  /** 頁面請求時的路徑(不根據CSR頁面轉導後更換)，ex:/baseHref/zh-tw/participate/75 */
  public pathname: string;
  /** 頁面請求時的查詢參數(不根據CSR頁面轉導後更換)，ex: {stage: aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA%3D} */
  public queryParams: Record<string, string>;
  /** 是否為編輯模式的 url */
  public isEditModeUrl: boolean;
  /** 是否為後台開啟預覽模式的 url */
  public isDemoModeUrl: boolean;
  /** 網站 basehref */
  public baseHref = '/';
  /** 網站首頁絕對路徑(含 baseHref)
   * 
   * 例1: baseHref: /，href: http://localhost:4200
   * 
   * => /
   * 
   * 例2: baseHref: /，href: http://localhost:4200/home
   * 
   * => /home/
   * 
   * 例3: baseHref: /tdoc/client/，href: http://localhost:4200/tdoc/client
   * 
   * => /tdoc/client/
   * 
   * 例4: baseHref: /tdoc/client/，href: http://localhost:4200/tdoc/client/home
   * 
   * => /tdoc/client/home/
   */
  public indexPagePathname: string;
  /** 網站首頁絕對路徑(不含 baseHref)
   * 
   * 例1: baseHref: /，href: http://localhost:4200
   * 
   * => /
   * 
   * 例2: baseHref: /，href: http://localhost:4200/home
   * 
   * => /home/
   * 
   * 例3: baseHref: /tdoc/client/，href: http://localhost:4200/tdoc/client
   * 
   * => /
   * 
   * 例4: baseHref: /tdoc/client/，href: http://localhost:4200/tdoc/client/home
   * 
   * => /home/
   */
  public indexPage4RouterPathname: string;
  /** 首頁 url */
  public indexPageUrl: string;
  /** 後台編輯器塞過來的資料 */
  public backStagePayload: BackStagePayload;
  public config: {
    /** 語系 */
    lang: string;
  } = {
    lang: 'zh-tw',
  };
  /** http://hostName:port */
  public origin: string;

  constructor(
    
  ) {
    if (!this.isBrowser) {
      this.loggerService.info(`isBrowser: ${this.isBrowser}; isServer: ${this.isServer}; isSSRMode: ${this.isSSRMode}`);
    }

    this.baseHref = this.document.head.getElementsByTagName('base')[0].getAttribute('href');

    if (this.isBrowser) {
      // location.href = 'http://theme.admin.goodays.cc:8100/index/1046/en-us/normal6/100392;demoToken=6f1572a3-433c-4648-aad3-8ae75006b69f'
      // location.href = 'http://demo.goodays.cc:8109/CP/zh-tw?stage=aHR0cCUzQSUyRiUyRnRoZW1lLmFkbWluLmdvb2RheXMuY2MlM0E4MTA5'
      this.url = location.href.replace('/?', '?').replace('/;', ';').replace(/\/$/g, '').replace(/\.html/g, '');
      this.siteUrl = location.href.replace('/?', '?').replace('/;', ';').replace(/\/$/g, '').replace(/\.html/g, '');
      // location.pathname = '/index/1046/en-us/normal6/100392;demoToken=6f1572a3-433c-4648-aad3-8ae75006b69f'
      // location.pathname = '/CP/zh-tw'
      this.pathname = removeAnchorHref(removeUrlQueryParam(location.pathname)).replace(/\/$/g, '').replace(/\.html/g, '');
      this.queryParams = parseQueryPqrams(this.url);
      this.isEditModeUrl = !!this.queryParams['stage'];
      this.isDemoModeUrl = !!this.queryParams['demoToken'];
      this.origin = location.origin;
    } else {
      this.url = `${this.request.protocol}://${this.request.get('host')}${this.request.originalUrl}`.replace('/?', '?').replace('/;', ';').replace(/\/$/g, '').replace(/\.html/g, '');
      this.siteUrl = `${environment.frontsideUrl}${this.request.originalUrl}`.replace(this.baseHref, '').replace('/?', '?').replace('/;', ';').replace(/\/$/g, '').replace(/\.html/g, '');
      this.loggerService.info(`url: ${this.url}`);
      // request._parsedUrl.pathname = /home/zh-tw/normal1;demoToken=bb5412ae-52e8-4602-b936-17afd2457b57
      // request._parsedUrl.pathname = /home/zh-tw/normal1
      this.pathname = removeAnchorHref(removeUrlQueryParam(this.request.path)).replace(/\/$/g, '').replace(/\.html/g, '');
      this.loggerService.info(`pathname: ${this.pathname}`);
      this.queryParams = parseQueryPqrams(this.url);
      this.loggerService.info(`queryParams: `, this.queryParams);
      this.isEditModeUrl = !!this.queryParams['stage'];
      this.isDemoModeUrl = !!this.queryParams['demoToken'];
      this.loggerService.info(`isEditModeUrl: ${this.isEditModeUrl}`);
      this.loggerService.info(`isDemoModeUrl: ${this.isDemoModeUrl}`);
      this.origin = `${this.request.protocol}://${this.request.get('host')}`;
      this.loggerService.info(`origin: ${this.origin}`);
    }

    const sitePathSegs = this.pathname.replace(this.baseHref, '').split('/');
    if (this.generatorEnv.mode === 'domain') {
      this.indexPagePathname = this.baseHref + (sitePathSegs[0] ? sitePathSegs[0] + '/' : '');
      this.indexPage4RouterPathname = '/' + (sitePathSegs[0] ? sitePathSegs[0] + '/' : '');
    } else {
      if (sitePathSegs.length > 1) {
        this.indexPagePathname = this.baseHref + sitePathSegs[0] + '/' + sitePathSegs[1] + '/';
        this.indexPage4RouterPathname = '/' + sitePathSegs[0] + '/' + sitePathSegs[1] + '/';
      } else {
        this.indexPagePathname = this.baseHref;
        this.indexPage4RouterPathname = '/';
      }
    }
    this.loggerService.info(`indexPagePathname: ${this.indexPagePathname}`);
    this.loggerService.info(`indexPage4RouterPathname: ${this.indexPage4RouterPathname}`);

    this.indexPageUrl = this.origin + (this.indexPagePathname || '');
    this.loggerService.info(`indexPageUrl: ${this.indexPageUrl}`);
  }

  getConfig(backStageSubj?: Subject<BackStagePayload>): Observable<{ lang: string; backStagePayload?: BackStagePayload; }> {
    // TODO: 根據 host 取得網站組成設定

    if (this.tState.hasKey(SITE_CONFIG_KEY)) {
      // 瀏覽器 SSR 開啟
      this.config = JSON.parse(this.tState.get(SITE_CONFIG_KEY, null));
      window['printAllState'] = this.tState.toJson();
    } else if (this.isServer) {
      // NODE SSR 開啟
      let urlSegArr: string[];
      let lang: string;
      if (this.generatorEnv.mode === 'domain') {
        urlSegArr = this.indexPage4RouterPathname.split('/');
        lang = urlSegArr[1];
      } else {
        urlSegArr = this.indexPage4RouterPathname.split('/');
        lang = urlSegArr[2];
      }
      this.config = { lang };
      this.tState.set(SITE_CONFIG_KEY, JSON.stringify(this.config));
    } else {
      // 瀏覽器 CSR 開啟
      let urlSegArr: string[];
      let lang: string;
      if (this.generatorEnv.mode === 'domain') {
        urlSegArr = this.indexPage4RouterPathname.split('/');
        lang = urlSegArr[1];
      } else {
        urlSegArr = this.indexPage4RouterPathname.split('/');
        lang = urlSegArr[2];
      }
      this.config = { lang };
    }

    if (backStageSubj) {
      // 從後台開的
      return backStageSubj.pipe(
        take(1),
        map(backStagePayload => {
          this.backStagePayload = backStagePayload;
          return this.config;
        }),
      );
    } else {
      // 直接進前台的
      return of(this.config);
    }
  }

  /** 取得當前網頁請求的網址
   * 只用來紀錄不可用來寫入到文件中的，要作寫入使用 getCurrentSiteUrl()
   */
  getCurrentUrl() {
    if (this.isServer) {
      return this.url;
    } else {
      return location.href;
    }
  }

  /** 取得當前訪問的頁面網址 */
  getCurrentSiteUrl() {
    if (this.isServer) {
      return this.siteUrl;
    } else {
      return location.href;
    }
  }

  /** 取得當前訪網頁請求的路徑 */
  getCurrentPathname() {
    if (this.isServer) {
      return this.pathname;
    } else {
      return location.pathname;
    }
  }

  /** 取得當前訪網頁請求的查詢參數 */
  getCurrentQueryParams() {
    if (this.isServer) {
      return this.queryParams;
    } else {
      const queryParamStr = location.search.slice(1, location.search.length);
      const queryParams = queryParamStr ? parseQueryPqrams(queryParamStr) : {};
      return queryParams;
    }
  }
}
